import { MinOrderQtyType } from '@/enums/MinOrderQtyType';
import CartMenu from '@/interfaces/CartMenu';
import CartMenuOption from '@/interfaces/CartMenuOption';
import Menu from '@/interfaces/Menu';
import { useCartStore } from '@/stores/cart';
import { useConfigStore } from '@/stores/config';
import { useMenuStore } from '@/stores/menu';
import { useModalStore } from '@/stores/modal';
import { useOrderStore } from '@/stores/order';

export function useMenu() {
  interface GridMenu extends Menu {
    index: number;
  }

  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();
  const orderStore = useOrderStore();

  //#region Private 함수
  /**
   * 메뉴 리스트에서 최대 행 수 조회
   * @param menus 메뉴 리스트
   * @returns 최대 행 수
   */
  const getMaxRowCount = (menus: Menu[]) => {
    const lastMenu = menus.at(-1);
    if (!lastMenu) return 0;
    return lastMenu.PositionZ * configStore.config.MenupanRowCount + lastMenu.PositionY + 1;
  };

  /**
   * 세트메뉴 그룹별 선택 항목이 1개만 존재한다면 자동으로 선택 후 장바구니 추가 후 성공여부 리턴
   * @param menu 선택한 메뉴 데이터
   * @param menuGroupIndex 선택한 메뉴 그룹 인덱스
   * @param menuIndex 선택한 메뉴 그룹내 메뉴 인덱스
   */
  const addToCartOrMoveSetMenu = async (menuGroupIndex: number, menuIndex: number, menu: Menu): Promise<void> => {
    // 선택한 메뉴의 모든 세트메뉴 옵션 조회
    const filterdSetMenus = menuStore.setMenus.filter((setMenu) => setMenu.ItemCode === menu.ItemCode);

    // 그룹코드를 Array로 생성
    const setMenuGroupCodes = filterdSetMenus.map((setMenu) => setMenu.GroupCode);

    // 그룹코드 Array 중복 제거
    const set = new Set(setMenuGroupCodes);

    // 중복 제거한 그룹코드 개수보다 많다면 2개 이상의 선택메뉴가 있다고 판단하고 메뉴 옵션 선택 모달 열기
    if (set.size !== setMenuGroupCodes.length) {
      modalStore.openFullScreenModal(menuStore.getMenuOptionComponent(), { menu });
      return;
    }

    // 메뉴판 읽기모드인 경우 아무동작도 하지 않는다
    if (configStore.isMobileMenu) return;

    const qty = 1;

    // 동일한 메뉴인지 구분하기 위해 세트메뉴 리스트 정렬
    filterdSetMenus.sort((a, b) => {
      return a.GroupCode - b.GroupCode || a.Name.localeCompare(b.Name) || Number(a.ItemCode) - Number(b.ItemCode);
    });

    // 장바구니에 추가
    const cartMenu: CartMenu = {
      GroupCode: menu.GroupCode,
      ItemCode: menu.ItemCode,
      Qty: qty,
      Toppings: [],
      SetMenus: filterdSetMenus.map((setMenu) => {
        const cartSetMenu: CartMenuOption = {
          MenuItemCode: setMenu.ItemCode,
          GroupCode: setMenu.GroupCode,
          OptionCode: setMenu.SetMenuCode,
          Qty: 1,
        };
        return cartSetMenu;
      }),
    };

    const isSuccess = await cartStore.addCartMenu(cartMenu);
    if (!isSuccess) return;

    animateAddToCart(menuGroupIndex, menuIndex);
  };

  /**
   * 일반메뉴 장바구니에 담기
   * @param menuGroupIndex 선택한 메뉴 그룹 인덱스
   * @param menuIndex 선택한 메뉴 그룹내 메뉴 인덱스
   * @param menu 선택한 메뉴 데이터
   */
  const addToCart = async (menuGroupIndex: number, menuIndex: number, menu: Menu): Promise<void> => {
    // 메뉴판 읽기모드인 경우 아무동작도 하지 않는다
    if (configStore.isMobileMenu) return;

    const qty = 1;

    // 장바구니에 메뉴 추가
    const isSuccess = await cartStore.addCartMenu({
      GroupCode: menu.GroupCode,
      ItemCode: menu.ItemCode,
      Qty: qty,
      Toppings: [],
      SetMenus: [],
    });
    if (!isSuccess) return;

    // 장바구니 추가 애니메이션 실행
    animateAddToCart(menuGroupIndex, menuIndex);
  };

  /**
   * 장바구니 담기 애니메이션 실행
   * @param menuGroupIndex 선택한 메뉴의 그룹 인덱스
   * @param menuIndex 선택한 메뉴의 그룹내 메뉴 인덱스
   */
  const animateAddToCart = (menuGroupIndex: number, menuIndex: number) => {
    const productItemParent = document.querySelector(`.product-${menuGroupIndex}${menuIndex}`);
    const productItem = productItemParent?.firstChild as HTMLDivElement;
    const cloneItem = productItemParent?.cloneNode(true).firstChild as HTMLElement;
    cloneItem.style.top = productItem.getBoundingClientRect().y + 'px';
    cloneItem.style.left = productItem.getBoundingClientRect().x + 'px';
    cloneItem.style.width = productItem.clientWidth + 'px';
    cloneItem.style.height = productItem.clientHeight + 'px';
    cloneItem?.classList.add('absolute');
    productItemParent?.appendChild(cloneItem);

    // 애니메이션 끝나면 clone Node 삭제
    setTimeout(function () {
      cloneItem.remove();
    }, 700);
  };
  //#endregion

  //#region Public 함수
  /**
   * 메뉴 그룹별 메뉴 리스트 조회
   * @param menuGroupCode 메뉴 그룹 코드
   * @returns 메뉴 리스트
   */
  const getMenusByGroup = (menuGroupCode: string) => {
    return menuStore.menus.filter((item) => item.GroupCode === menuGroupCode);
  };

  /**
   * 메뉴 리스트를 그리드로 변환
   * @param menus 메뉴 리스트
   * @returns 메뉴 그리드
   */
  const convertMenuListToGrid = (menuGroupCode: string) => {
    // 메뉴 그룹별 메뉴 리스트 조회
    const menus = getMenusByGroup(menuGroupCode);

    // 설정한 행 수와 열 수에 맞게 비어있는 메뉴 그리드를 2차원 배열로 생성
    const grid: (GridMenu | null)[][] = Array.from({ length: getMaxRowCount(menus) }, () =>
      Array.from({ length: configStore.config.MenupanColumnCount }, () => null),
    );

    // 메뉴 리스트를 순회하며 메뉴 그리드에 메뉴를 배치
    menus.forEach((menu, index) => {
      // 페이지가 없기 때문에 메뉴의 z축 위치를 y축으로 변환하여 실제 행 위치를 계산
      const menuPositionY = menu.PositionZ * configStore.config.MenupanRowCount + menu.PositionY;

      // 계산한 위치에 메뉴를 배치
      grid[menuPositionY][menu.PositionX] = { ...menu, index };
    });

    // 메뉴 그리드 반환
    return grid.filter((row) => row.some((cell) => cell !== null));
  };

  /**
   * 선택한 메뉴 유형에 따라 동작을 수행한다
   * 토핑 메뉴라면 토핑 선택 모달로 이동
   * 세트 메뉴라면 세트 메뉴 옵션 선택 모달로 이동
   * 일반 메뉴라면 바로 장바구니에 담는다
   * @param menuGroupIndex 메뉴 그룹 키
   * @param menuIndex 메뉴 키
   * @param menu 선택한 메뉴
   */
  const selectMenu = async (menuGroupIndex: number, menuIndex: number, menu: Menu) => {
    switch (menu.MenuType) {
      case 'M':
        // 토핑 메뉴인 경우 메뉴 옵션 선택 모달 열기
        modalStore.openFullScreenModal(menuStore.getMenuOptionComponent(), { menu });
        break;
      case 'S':
        // 세트 메뉴인 경우 모든 그룹의 선택 옵션이 1개씩만 있다면 자동으로 선택하고 장바구니에 담는다
        // 1개의 그룹이라도 2개 이상의 선택 옵션이 존재한다면 메뉴 상세 페이지로 이동
        await addToCartOrMoveSetMenu(menuGroupIndex, menuIndex, menu);
        break;
      default:
        await addToCart(menuGroupIndex, menuIndex, menu);
        break;
    }
  };

  /**
   * 장바구니 내부에서 수량 조정 시 사용할 최소주문수량 계산
   * @param menu  메뉴 데이터
   * @returns 최소주문수량
   */
  const calcCartMinOrderQty = (menu: Menu): number => {
    switch (configStore.config.MinOrderQtyType) {
      case MinOrderQtyType.FirstOnly: {
        // 첫 주문 시에만 최소주문수량 사용 옵션인 경우 이미 주문한 메뉴의 수량 조회하여 적용
        const orderedQty = orderStore.getOrderedQty(menu.ItemCode);
        return menu.MinOrderQty - orderedQty <= 0 ? 1 : menu.MinOrderQty - orderedQty;
      }
      case MinOrderQtyType.Always:
        // 항상 최소주문수량 사용
        return menu.MinOrderQty || 1;
      default:
        return 1;
    }
  };

  /**
   * 메뉴 품절 여부 조회
   * @param menu 조회하려는 메뉴 데이터
   * @returns 품절 여부
   */
  const isMenuSoldOut = (menu: Menu): boolean => {
    // 품절 검사
    if (menu.IsSoldOut) return true;

    // 한정 수량 조회
    const limitItem = menuStore.getLimitItem(menu.ItemCode);

    // 한정 수량 검사
    if (limitItem) {
      // 최소 주문 수량 조회
      const minOrderQty = calcCartMinOrderQty(menu);
      // 한정 수량 조회
      const cartQty = cartStore.cart.CartMenus.find((cartMenu) => cartMenu.ItemCode === menu.ItemCode)?.Qty || 0;
      if (limitItem.OrderLimitCnt <= cartQty) return true;
      if (limitItem.OrderLimitCnt - cartQty < minOrderQty) return true;
    }

    return false;
  };

  /**
   * 주메뉴 배지 표시 여부
   * @param menu 메뉴 데이터
   * @returns 주메뉴 배지 표시 여부
   */
  const isMainMenuBadgeActive = (menu: Menu): boolean => {
    const { useOneMenuPerPerson, config } = configStore;
    // 1인 1메뉴 설정이거나 디저트 타입이 주메뉴(2)인 경우
    const isActive = useOneMenuPerPerson || config.DessertType === '2';
    // 해당 메뉴에 설정된 주메뉴 수량이 0보다 큰 경우
    const mainMenuQuantity = (menu.MainMenuQty ?? 0) > 0;

    return isActive && mainMenuQuantity;
  };

  /**
   * 후식 주문 가능 여부 조회
   * @param menu 메뉴 데이터
   * @returns 후식 주문 가능 여부
   */
  const isDessertOrderable = (menu: Menu) => {
    // 후식 메뉴가 아니라면 항상 주문 가능
    if (!menu.IsDessert) return true;

    switch (configStore.config.DessertType) {
      // 후식 메뉴 옵션 사용하지 않는 경우 주문 가능
      case '0':
        return true;
      // 기존 주문이 존재한다면 주문 가능
      case '1':
        return (orderStore.order.Items?.length ?? 0) > 0;
      // 기존 주문에 주메뉴가 존재한다면 주문 가능
      case '2': {
        return orderStore.order.Items?.some((item) => {
          const orderedMenu = menuStore.menus.find((m) => m.ItemCode === item.ITEM_CODE);
          console.log(orderedMenu);
          return (orderedMenu?.MainMenuQty ?? 0) > 0;
        });
      }
      default:
        throw new Error('DessertType이 올바르지 않습니다.');
    }
  };

  /**
   * 선택한 메뉴가 주문이 가능한지 여부 (품절 또는 주메뉴 여부 검사)
   * @param menu 메뉴 데이터
   * @returns 주문 가능 여부
   */
  const isMenuOrderable = (menu: Menu) => {
    // 품절된 메뉴인지 검사
    if (isMenuSoldOut(menu)) return false;

    // 후식 메뉴인 경우 후식 주문 가능 여부 검사
    if (!isDessertOrderable(menu)) return false;

    return true;
  };
  //#endregion

  return {
    getMenusByGroup,
    convertMenuListToGrid,
    selectMenu,
    calcCartMinOrderQty,
    isMenuSoldOut,
    isMainMenuBadgeActive,
    isDessertOrderable,
    isMenuOrderable,
  };
}
